import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Sun, Moon, Monitor, CalendarPlus, Download, Trash2, AlertTriangle, EyeOff, LogOut, UserCog, Copy, Undo2, ShieldCheck, Bell, ChevronDown, PlayCircle, SlidersHorizontal, Pencil, Check, X, Upload, Database, Wand2, ZoomIn, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DuplicateFinder } from "@/components/DuplicateFinder";
import { TourDuplicateFinder } from "@/components/TourDuplicateFinder";
import { DataImport } from "@/components/DataImport";
import { NotionSync } from "@/components/NotionSync";
import { QuickImportShortcut } from "@/components/QuickImportShortcut";
import { TrashBin } from "@/components/TrashBin";
import AppLockSettings from "@/components/AppLockSettings";
import { NotificationSettings } from "@/components/NotificationSettings";
import { FieldManager } from "@/components/FieldManager";
import { DisplaySize } from "@/components/DisplaySize";
import { InstallSteps } from "@/components/OnboardingDialog";
import { Smartphone } from "lucide-react";
import { useFieldConfig } from "@/contexts/FieldConfigContext";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { useTheme } from "@/contexts/ThemeContext";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { useAuth } from "@/contexts/AuthContext";
import { parseTourDates, rangeBounds, tourTitle } from "@/lib/fields";
import { resetHints } from "@/lib/hints";
import { LegalLinks } from "@/components/LegalDialog";
import { api, formatApiError } from "@/lib/api";
import { downloadFullBackup } from "@/lib/fullBackup";
import { LOGOUT } from "@/constants/testIds/auth";

const Section = ({ icon: Icon, title, desc, children, collapsible = false, defaultOpen = true, testid }) => {
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div
        className={`flex items-start gap-3 ${open ? "mb-4" : ""} ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        data-testid={collapsible ? testid : undefined}
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-800 text-[#E0575B]">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-700 tracking-tight text-zinc-100">{title}</h3>
          {desc && <p className="text-xs text-zinc-400">{desc}</p>}
        </div>
        {collapsible && (
          <ChevronDown className={`mt-0.5 h-5 w-5 shrink-0 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        )}
      </div>
      {open && children}
    </div>
  );
};

const GroupHeader = ({ label }) => (
  <p className="px-1 pt-2 text-[11px] font-700 uppercase tracking-wider text-zinc-500">{label}</p>
);

const csvCols = [
  ["Tour Name", (t) => tourTitle(t)],
  ["Guest Name", (t) => t.guest_name || ""],
  ["Secondary Guests", (t) => (Array.isArray(t.linked_guests) ? t.linked_guests : []).filter(Boolean).join("; ")],
  ["Booking Status", (t) => t.booking_status || ""],
  ["Tour Date", (t) => (Array.isArray(t.tour_date) ? t.tour_date.join("; ") : t.tour_date || "")],
  ["Resort / Meet", (t) => t.resort_meet || ""],
  ["Meeting Time", (t) => t.meeting_time || ""],
  ["Shift Start", (t) => t.shift_start || ""],
  ["Shift End", (t) => t.shift_end || ""],
  ["Return", (t) => (t.return_trip ? "Yes" : "No")],
  ["Days", (t) => t.days ?? ""],
  ["Party Size", (t) => t.party_size ?? ""],
  ["Guest List", (t) => (Array.isArray(t.guest_list) ? t.guest_list : [])
    .filter((g) => g && ((g.name && String(g.name).trim()) || (g.age !== null && g.age !== undefined && g.age !== "")))
    .map((g) => {
      const nm = (g.name && String(g.name).trim()) || "Unnamed";
      const age = (g.age !== null && g.age !== undefined && g.age !== "") ? ` (age ${g.age})` : "";
      return `${nm}${age}`;
    })
    .join("; ")],
  ["Co-Guide", (t) => t.co_guide || ""],
  ["Type", (t) => t.tour_type || ""],
  ["Cost Type", (t) => t.cost_type || ""],
  ["Cost / Hour", (t) => t.cost_per_hour ?? ""],
  ["Total Hours", (t) => t.total_hours ?? ""],
  ["Comp Time", (t) => t.comp_time ?? ""],
  ["Rating", (t) => t.rating ?? ""],
  ["Gratuity", (t) => t.gratuity ?? ""],
  ["Tip %", (t) => t.tip_percent ?? ""],
  ["Tip Type", (t) => t.tip_type || ""],
  ["Avg Gratuity / Day", (t) => t.avg_per_guest ?? ""],
  ["Retail Value", (t) => t.retail_value ?? ""],
  ["Host Again", (t) => (t.host_again === false ? "No" : "Yes")],
  ["Notes", (t) => t.notes || ""],
];

const toCSV = (tours, customFields = []) => {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const customCols = customFields.map((cf) => [cf.label, (t) => {
    const v = t[cf.key];
    if (v === null || v === undefined || v === "") return "";
    if (cf.type === "bool") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.join("; ");
    return String(v);
  }]);
  const cols = [...csvCols, ...customCols];
  const header = cols.map((c) => esc(c[0])).join(",");
  const rows = tours.map((t) => cols.map((c) => esc(c[1](t))).join(","));
  return [header, ...rows].join("\n");
};

const fmtCelebrations = (list) => (Array.isArray(list) ? list : [])
  .filter((c) => c && (c.month || c.month === 0) && c.day)
  .map((c) => {
    const who = (c.name && String(c.name).trim()) ? `${String(c.name).trim()} — ` : "";
    const type = c.type === "anniversary" ? "Anniversary" : "Birthday";
    const date = `${c.month}/${c.day}${c.year ? `/${c.year}` : ""}`;
    const note = (c.note && String(c.note).trim()) ? ` (${String(c.note).trim()})` : "";
    return `${who}${type} ${date}${note}`;
  })
  .join("; ");

const guestCsvCols = [
  ["Guest Name", (p) => p.name || ""],
  ["Also Toured With", (p) => (Array.isArray(p.linked) ? p.linked : []).filter(Boolean).join("; ")],
  ["City", (p) => p.city || ""],
  ["State", (p) => p.state || ""],
  ["Country", (p) => p.country || ""],
  ["Email", (p) => p.mdx_email || ""],
  ["Phone", (p) => p.phone || ""],
  ["Lifetime Tours", (p) => p.lifetime_tours ?? ""],
  ["Lifetime Days", (p) => p.lifetime_days ?? ""],
  ["Lifetime Gratuity", (p) => p.lifetime_gratuity ?? ""],
  ["Avg Daily Gratuity", (p) => p.average_daily_gratuity ?? ""],
  ["Celebrations", (p) => fmtCelebrations(p.celebrations)],
  ["Notes", (p) => p.notes || ""],
];

const toGuestCSV = (profiles) => {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = guestCsvCols.map((c) => esc(c[0])).join(",");
  const rows = profiles.map((p) => guestCsvCols.map((c) => esc(c[1](p))).join(","));
  return [header, ...rows].join("\n");
};

export const Settings = ({ onDataCleared, focusExport }) => {
  const { theme, setTheme } = useTheme();
  const { financialPrivacy, setFinancialPrivacy } = usePrivacy();
  const { user, setUser, logout } = useAuth();
  const { customFields } = useFieldConfig();
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [exportRange, setExportRange] = useState("all");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [clearStep, setClearStep] = useState(0);
  const [confirmText, setConfirmText] = useState("");
  const [delStep, setDelStep] = useState(0);
  const [delConfirmText, setDelConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [dupSignal, setDupSignal] = useState(0);
  const [lastImport, setLastImport] = useState(null); // { batch, count }
  const [lastCalloutImport, setLastCalloutImport] = useState(null); // { batch, count }
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPending = () => {
    if (user?.role === "admin") api.adminPendingCount().then((r) => setPendingCount(r.count || 0)).catch(() => {});
  };
  useEffect(() => { refreshPending(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.role]);

  const afterImport = async (count, batchId) => {
    if (batchId) setLastImport({ batch: batchId, count });
    onDataCleared?.(); // refresh the tour/guest lists
    try {
      const [g, t] = await Promise.all([api.duplicateProfiles(), api.tourDuplicates()]);
      if (g.length || t.length) {
        toast.warning(
          `Found ${g.length} possible duplicate guest${g.length === 1 ? "" : "s"} and ${t.length} duplicate tour group${t.length === 1 ? "" : "s"}`,
          { description: "Review them below in Data cleanup.", duration: 9000 }
        );
        if (g.length) setDupSignal((s) => s + 1); // auto-open the guest checker
      } else {
        toast.success("Import looks clean — no duplicates found.");
      }
    } catch { /* ignore */ }
  };

  const undoLastImport = async () => {
    if (!lastImport) return;
    try {
      const res = await api.undoImport(lastImport.batch);
      toast.success(`Undid import — removed ${res.deleted} tour${res.deleted === 1 ? "" : "s"}`);
      setLastImport(null);
      onDataCleared?.();
    } catch {
      toast.error("Could not undo the import");
    }
  };

  const afterCalloutImport = (count, batchId) => {
    if (batchId) setLastCalloutImport({ batch: batchId, count });
    // Jump to Call Outs and focus the FMLA card so the updated balance is visible.
    try { localStorage.setItem("sd_focus_fmla", "1"); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event("sd:goto-callouts"));
  };
  const undoCalloutImport = async () => {
    if (!lastCalloutImport) return;
    try {
      const res = await api.undoCalloutImport(lastCalloutImport.batch);
      toast.success(`Undid import — removed ${res.deleted} call-out${res.deleted === 1 ? "" : "s"}`);
      setLastCalloutImport(null);
    } catch {
      toast.error("Could not undo the import");
    }
  };

  const feedToken = user?.feed_token || "";

  const httpsFeed = (path) => `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "")}${path}?token=${encodeURIComponent(feedToken)}`;

  const appleCalendar = () => {
    const app = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, "webcal:");
    const url = `${base}/api/calendar.ics?app=${encodeURIComponent(app)}&token=${encodeURIComponent(feedToken)}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    window.location.href = url;
    toast.success("Opening Apple Calendar subscription (link copied too)");
  };

  const appleStandbyCalendar = () => {
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, "webcal:");
    const url = `${base}/api/calendar-standby.ics?token=${encodeURIComponent(feedToken)}`;
    navigator.clipboard?.writeText(url).catch(() => {});
    window.location.href = url;
    toast.success("Opening Standby calendar subscription (link copied too)");
  };

  const copyFeedLink = (path, label) => {
    navigator.clipboard?.writeText(httpsFeed(path)).catch(() => {});
    toast.success(`${label} link copied — in Google Calendar add it via Other calendars → From URL`);
  };

  const startEditName = () => { setNameDraft(user?.name || ""); setEditingName(true); };
  const saveName = async () => {
    const next = nameDraft.trim();
    if (!next) { toast.error("Name can't be empty"); return; }
    if (next === (user?.name || "")) { setEditingName(false); return; }
    setSavingName(true);
    try {
      const res = await api.updateMyName(next);
      setUser(res.user);
      toast.success("Name updated");
      setEditingName(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update name");
    } finally {
      setSavingName(false);
    }
  };

  const exportCsv = async () => {    try {
      const all = await api.listGuests();
      const b = rangeBounds(exportRange, exportFrom, exportTo);
      const filtered = !b
        ? all
        : all.filter((t) => {
            const [from, to] = b;
            const ds = parseTourDates(t.tour_date);
            return ds.length && ds.some((d) => (!from || d >= from) && (!to || d <= to));
          });
      if (!filtered.length) {
        toast.error("No tours found in that date range");
        return;
      }
      const blob = new Blob([toCSV(filtered, customFields)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `disney-tours-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filtered.length} tour(s)`);
    } catch {
      toast.error("Export failed");
    }
  };

  const exportGuestsCsv = async () => {
    try {
      const profiles = await api.listProfiles();
      if (!profiles.length) {
        toast.error("No guests to export");
        return;
      }
      const blob = new Blob([toGuestCSV(profiles)], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `disney-guests-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${profiles.length} guest(s)`);
    } catch {
      toast.error("Export failed");
    }
  };

  const [exportingAll, setExportingAll] = useState(false);
  const exportAllData = async () => {
    setExportingAll(true);
    await downloadFullBackup();
    setExportingAll(false);
  };

  useEffect(() => {
    if (focusExport) setTimeout(() => document.getElementById("export-section")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  }, [focusExport]);

  const doClear = async () => {
    try {
      await api.clearAll();
      toast.success("All data cleared");
      setClearStep(0);
      setConfirmText("");
      onDataCleared && onDataCleared();
    } catch {
      toast.error("Could not clear data");
    }
  };

  const doDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.deleteAccount();
      toast.success("Your account and data have been deleted");
      await logout();  // clears session cookie, API cache, and redirects to /login
    } catch {
      toast.error("Could not delete your account — please try again");
      setDeleting(false);
    }
  };

  const themeBtn = (mode, Icon, label) => (
    <button
      onClick={() => setTheme(mode)}
      data-testid={`theme-${mode}-btn`}
      className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-3 text-sm transition-colors ${
        theme === mode ? "border-[#9B2226] bg-[#9B2226]/10 text-[#E0575B]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-zinc-200"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="settings-view">
      <div>
        <h2 className="font-display text-2xl font-800 tracking-tight text-zinc-50">Settings</h2>
        <p className="text-sm text-zinc-400">Personalize the app and manage your data.</p>
      </div>

      {/* ---------- Account & Security ---------- */}
      <div className="space-y-3">
        <GroupHeader label="Account & Security" />
        <Section icon={UserCog} title="Account" desc="You're signed in with Google. Sessions last about a week." collapsible defaultOpen={false} testid="toggle-account-section">
          <div className="mb-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid="account-name-row">
            <p className="mb-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500">Display name</p>
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                  maxLength={80}
                  placeholder="Your name"
                  className="h-9 border-zinc-700 bg-zinc-950 text-sm text-zinc-100"
                  data-testid="account-name-input"
                />
                <Button onClick={saveName} disabled={savingName} size="sm" className="shrink-0 bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="account-name-save">
                  <Check className="h-4 w-4" />
                </Button>
                <Button onClick={() => setEditingName(false)} disabled={savingName} size="sm" variant="outline" className="shrink-0 border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="account-name-cancel">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-600 text-zinc-100" data-testid="account-name">{user?.name || "(no name)"}</p>
                <button onClick={startEditName} className="inline-flex shrink-0 items-center gap-1 rounded-md border border-zinc-700 px-2.5 py-1.5 text-xs font-600 text-zinc-300 transition-colors hover:text-[#E0575B]" data-testid="account-name-edit">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-600 text-zinc-100" data-testid="account-email">{user?.email}</p>
              <p className="text-xs text-zinc-400">{user?.role === "admin" ? "Owner account" : "Member"}</p>
            </div>
            <Button onClick={logout} variant="outline" className="shrink-0 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid={LOGOUT.button}>
              <LogOut className="mr-1.5 h-4 w-4" /> Log out
            </Button>
          </div>
          <button
            onClick={() => window.dispatchEvent(new Event("sd:onboarding"))}
            className="mt-2 flex w-full items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
            data-testid="show-tutorial-btn"
          >
            <PlayCircle className="h-4 w-4 text-[#E0575B]" /> Show tutorial again
          </button>
          <button
            onClick={() => { resetHints(); toast.success("All tips & hints reset — reloading…"); setTimeout(() => window.location.reload(), 900); }}
            className="mt-2 flex w-full items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
            data-testid="reset-hints-btn"
          >
            <RotateCcw className="h-4 w-4 text-[#E0575B]" /> Reset tips &amp; hints
          </button>
          <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-300" data-testid="settings-legal-row">
            <LegalLinks linkClassName="font-600 text-[#E0575B] underline hover:text-[#E0575B]/80" />
          </div>
        </Section>

        <Section icon={ShieldCheck} title="App Lock" desc="Add a passcode and Face ID / Touch ID on top of your Google login." collapsible defaultOpen={false} testid="toggle-applock-section">
          <AppLockSettings />
        </Section>

        <Section icon={EyeOff} title="Financial Privacy" desc="Hide your tips, tip %, and gratuity everywhere so you can safely show tour or guest details to someone else." collapsible defaultOpen={false} testid="toggle-privacy-section">
          <label className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid="financial-privacy-row">
            <span className="text-sm text-zinc-200">{financialPrivacy ? "Tips are hidden across the app" : "Tips are visible"}</span>
            <Switch checked={financialPrivacy} onCheckedChange={setFinancialPrivacy} data-testid="financial-privacy-toggle" className="data-[state=checked]:bg-[#9B2226]" />
          </label>
        </Section>
      </div>

      {/* ---------- Display ---------- */}
      <div className="space-y-3">
        <GroupHeader label="Display" />
        <Section icon={theme === "light" ? Sun : theme === "system" ? Monitor : Moon} title="Appearance" desc="Light, dark, or follow your device automatically.">
          <div className="grid grid-cols-3 gap-2">
            {themeBtn("light", Sun, "Light")}
            {themeBtn("dark", Moon, "Dark")}
            {themeBtn("system", Monitor, "Auto")}
          </div>
        </Section>

        <Section icon={ZoomIn} title="Display size" desc="Make everything bigger or smaller — handy on the Home Screen app where pinch-to-zoom is off.">
          <DisplaySize />
        </Section>

        <Section icon={SlidersHorizontal} title="Tour Sheet Fields" desc="Hide fields you never use, or add your own custom fields to track anything the sheet doesn't cover." collapsible defaultOpen={false} testid="toggle-field-section">
          <FieldManager />
        </Section>

        <Section icon={Bell} title="Push Notifications" desc="Turn on alerts for this device, then choose what you want to be notified about." collapsible defaultOpen={false} testid="toggle-notifications-section">
          <NotificationSettings />
        </Section>

        <Section icon={Smartphone} title="Install on your phone" desc="Add Stage Door to your home screen so it opens like a real app (works offline)." collapsible defaultOpen={false} testid="toggle-install-section">
          <div className="mt-1"><InstallSteps /></div>
        </Section>
      </div>

      {/* ---------- Calendar, Backup & Export ---------- */}
      <div className="space-y-3">
        <GroupHeader label="Calendar, Backup & Export" />
        <Section icon={CalendarPlus} title="Calendar Sync" desc="Subscribe to your tours & shifts on any device — it auto-updates." collapsible defaultOpen={false} testid="toggle-calendar-section">
          <div className="space-y-3">
            <div>
              <p className="mb-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500">Tours &amp; shifts</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={appleCalendar} variant="outline" className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800" data-testid="settings-apple-calendar-btn">
                  <CalendarPlus className="mr-1.5 h-4 w-4" /> Add to Apple Calendar
                </Button>
                <Button onClick={() => copyFeedLink("/api/calendar.ics", "Tours & shifts")} variant="outline" className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800" data-testid="settings-copy-calendar-btn">
                  <Copy className="mr-1.5 h-4 w-4" /> Copy link (Android / Google)
                </Button>
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-600 uppercase tracking-wide text-zinc-500">Standby (separate)</p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={appleStandbyCalendar} variant="outline" className="border-amber-700/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" data-testid="settings-apple-standby-btn">
                  <CalendarPlus className="mr-1.5 h-4 w-4" /> Add to Apple Calendar
                </Button>
                <Button onClick={() => copyFeedLink("/api/calendar-standby.ics", "Standby")} variant="outline" className="border-amber-700/50 bg-transparent text-amber-300 hover:bg-amber-500/10" data-testid="settings-copy-standby-btn">
                  <Copy className="mr-1.5 h-4 w-4" /> Copy link (Android / Google)
                </Button>
              </div>
            </div>
            <details className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-xs text-zinc-400" data-testid="calendar-help">
              <summary className="cursor-pointer font-600 text-zinc-300">How to subscribe on each device</summary>
              <div className="mt-2 space-y-2">
                <p><span className="font-600 text-zinc-200">iPhone / iPad / Mac:</span> tap “Add to Apple Calendar” — it opens Apple Calendar and asks you to subscribe. It refreshes automatically every few hours.</p>
                <p><span className="font-600 text-zinc-200">Android / Google Calendar:</span> tap “Copy link”, then on a computer open Google Calendar → <span className="text-zinc-300">Other calendars → From URL</span>, paste, and Add. It’ll then show on your Android phone. (Google refreshes subscribed URLs roughly every 8–24 hours.)</p>
                <p>Future requests appear as tentative all-day placeholders; standby shifts can be kept in their own calendar so you can toggle them on/off.</p>
              </div>
            </details>
          </div>
        </Section>

        <Section icon={Wand2} title="Quick Import (iPhone Shortcut)" desc="Share a screenshot straight to the app — it auto-creates the tour. No saving or opening needed." collapsible defaultOpen={false} testid="toggle-shortcut-section">
          <QuickImportShortcut />
        </Section>

        <Section icon={Database} title="Notion Backup" desc="Keep a live copy of all your tours in your own Notion. One-way — it never changes your app data." collapsible defaultOpen={false} testid="toggle-notion-section">
          <NotionSync onSynced={onDataCleared} />
        </Section>

        <div id="export-section" className="scroll-mt-24">
        <Section key={focusExport ? "focus" : "normal"} icon={Download} title="Export Data (CSV)" desc="Download your tours or guests for backups or taxes." collapsible defaultOpen={!!focusExport} testid="toggle-export-section">
          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-600 uppercase tracking-wider text-zinc-500">Tours</p>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={exportRange} onValueChange={setExportRange}>
                  <SelectTrigger className="w-[160px] border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70" data-testid="export-range-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-950">
                    <SelectItem value="all" data-testid="export-range-all">All tours</SelectItem>
                    <SelectItem value="week" data-testid="export-range-week">This week</SelectItem>
                    <SelectItem value="month" data-testid="export-range-month">This month</SelectItem>
                    <SelectItem value="custom" data-testid="export-range-custom">Custom range…</SelectItem>
                  </SelectContent>
                </Select>
                {exportRange === "custom" && (
                  <div className="flex items-center gap-1.5" data-testid="export-custom-inputs">
                    <SingleDatePicker value={exportFrom} onChange={setExportFrom} testid="export-from-input" placeholder="From" className="w-[150px]" />
                    <span className="text-xs text-zinc-400">to</span>
                    <SingleDatePicker value={exportTo} onChange={setExportTo} testid="export-to-input" placeholder="To" className="w-[150px]" />
                  </div>
                )}
                <Button onClick={exportCsv} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="export-csv-btn">
                  <Download className="mr-1.5 h-4 w-4" /> Export Tours
                </Button>
              </div>
            </div>
            <div className="border-t border-zinc-800 pt-4">
              <p className="mb-1.5 text-xs font-600 uppercase tracking-wider text-zinc-500">Guests</p>
              <p className="mb-2 text-xs text-zinc-400">Every guest with contact info, lifetime tours & gratuity, celebrations and notes.</p>
              <Button onClick={exportGuestsCsv} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="export-guests-csv-btn">
                <Download className="mr-1.5 h-4 w-4" /> Export Guests
              </Button>
            </div>
            <div className="border-t border-zinc-800 pt-4">
              <p className="mb-1.5 text-xs font-600 uppercase tracking-wider text-zinc-500">Full backup (for the iOS app)</p>
              <p className="mb-2 text-xs text-zinc-400">A complete ZIP of everything — tours, guests, photos, shifts and time & attendance — that the Stage Door iOS app can import.</p>
              <Button onClick={exportAllData} disabled={exportingAll} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E] disabled:opacity-50" data-testid="export-all-data-btn">
                <Download className="mr-1.5 h-4 w-4" /> {exportingAll ? "Bundling…" : "Export All Data (ZIP)"}
              </Button>
            </div>
          </div>
        </Section>
        </div>
      </div>

      {/* ---------- Your Data ---------- */}
      <div className="space-y-3">
        <GroupHeader label="Your Data" />
        <Section icon={Upload} title="Import & cleanup" desc="Import tours & guests or call-outs / points & FMLA history from a file — then catch accidental duplicates." collapsible defaultOpen={false} testid="toggle-import-section">
          <div className="space-y-2">
            <DataImport onToursImported={afterImport} onCalloutsImported={afterCalloutImport} />
            {lastImport && (
              <button
                onClick={undoLastImport}
                className="flex w-full items-center justify-between rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300 transition-colors hover:bg-red-500/10"
                data-testid="undo-import-btn"
              >
                <span className="flex items-center gap-2"><Undo2 className="h-4 w-4" /> Undo last tour import ({lastImport.count} tour{lastImport.count === 1 ? "" : "s"})</span>
                <span className="text-xs text-red-400/70">Remove</span>
              </button>
            )}
            {lastCalloutImport && (
              <button
                onClick={undoCalloutImport}
                className="flex w-full items-center justify-between rounded-lg border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300 transition-colors hover:bg-red-500/10"
                data-testid="undo-callout-import-btn"
              >
                <span className="flex items-center gap-2"><Undo2 className="h-4 w-4" /> Undo last call-out import ({lastCalloutImport.count} call-out{lastCalloutImport.count === 1 ? "" : "s"})</span>
                <span className="text-xs text-red-400/70">Remove</span>
              </button>
            )}
            <DuplicateFinder onMerged={onDataCleared} openSignal={dupSignal} />
            <TourDuplicateFinder onChanged={onDataCleared} />
          </div>
        </Section>

        <Section icon={Trash2} title="Trash" desc="Deleted tours are kept here for 30 days so you can restore them. After that they're permanently removed." collapsible defaultOpen={false} testid="toggle-trash-section">
          <TrashBin onChanged={onDataCleared} />
        </Section>

        <Section icon={AlertTriangle} title="Danger Zone" desc="Permanently erase every tour, guest and shift. There is no undo." collapsible defaultOpen={false} testid="toggle-danger-section">
          <Button onClick={() => setClearStep(1)} variant="outline" className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 hover:text-red-300" data-testid="clear-data-btn">
            <Trash2 className="mr-1.5 h-4 w-4" /> Clear all data
          </Button>
          <div className="mt-4 border-t border-zinc-800 pt-4">
            <p className="mb-2 text-xs text-zinc-500">Delete your account and everything in it — tours, guests, shifts, call-outs and settings. You'll be signed out. This cannot be undone.</p>
            <Button onClick={() => setDelStep(1)} variant="outline" className="border-red-500/40 bg-red-500/5 text-red-400 hover:bg-red-500/15 hover:text-red-300" data-testid="delete-account-btn">
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete my account &amp; data
            </Button>
          </div>
        </Section>
      </div>

      {/* Step 1 */}
      <AlertDialog open={clearStep === 1} onOpenChange={(o) => !o && setClearStep(0)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="clear-dialog-1">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Clear all data?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">This permanently deletes every tour, guest profile, and shift. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="clear-cancel-1">Cancel</AlertDialogCancel>
            <Button onClick={() => setClearStep(2)} className="bg-red-600 text-white hover:bg-red-700" data-testid="clear-next-1">Continue</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Step 2 */}
      <AlertDialog open={clearStep === 2} onOpenChange={(o) => !o && setClearStep(0)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="clear-dialog-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">Seriously — all of your logged tours and guests will be gone forever. Consider exporting a CSV first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="clear-cancel-2">Keep my data</AlertDialogCancel>
            <Button onClick={() => setClearStep(3)} className="bg-red-600 text-white hover:bg-red-700" data-testid="clear-next-2">Yes, continue</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Step 3 — type to confirm */}
      <AlertDialog open={clearStep === 3} onOpenChange={(o) => { if (!o) { setClearStep(0); setConfirmText(""); } }}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="clear-dialog-3">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Type DELETE to confirm</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">Last chance. Type <b className="text-red-400">DELETE</b> below to erase everything.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="border-zinc-800 bg-zinc-900 focus-visible:ring-red-500" data-testid="clear-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="clear-cancel-3" onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
            <Button disabled={confirmText !== "DELETE"} onClick={doClear} className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" data-testid="clear-confirm-final">Delete everything</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete account — step 1: warn */}
      <AlertDialog open={delStep === 1} onOpenChange={(o) => !o && setDelStep(0)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="delete-account-dialog-1">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">This permanently deletes your account and <b>all</b> of your data — tours, guest profiles, shifts, call-outs, notifications and settings. You'll be signed out immediately. This cannot be undone. Consider exporting a CSV first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="delete-account-cancel-1">Keep my account</AlertDialogCancel>
            <Button onClick={() => setDelStep(2)} className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-account-next-1">Continue</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete account — step 2: type to confirm */}
      <AlertDialog open={delStep === 2} onOpenChange={(o) => { if (!o) { setDelStep(0); setDelConfirmText(""); } }}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-950" data-testid="delete-account-dialog-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Type DELETE to confirm</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">Last chance. Type <b className="text-red-400">DELETE</b> below to permanently delete your account and everything in it.</AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={delConfirmText} onChange={(e) => setDelConfirmText(e.target.value)} placeholder="DELETE" className="border-zinc-800 bg-zinc-900 focus-visible:ring-red-500" data-testid="delete-account-confirm-input" />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" data-testid="delete-account-cancel-2" onClick={() => setDelConfirmText("")}>Cancel</AlertDialogCancel>
            <Button disabled={delConfirmText !== "DELETE" || deleting} onClick={doDeleteAccount} className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" data-testid="delete-account-confirm-final">
              {deleting ? "Deleting…" : "Delete my account"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <p className="pt-2 pb-4 text-center text-[11px] leading-relaxed text-zinc-500" data-testid="settings-disney-disclaimer">
        Stage Door is an independent personal tool and is not affiliated with, endorsed by, or associated with The Walt Disney Company.
      </p>
    </div>
  );
};
